import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const TermsOfService = () => {
  const sections = [
    {
      title: "1. Acceptance of Terms",
      content: `By accessing or using the DataNodes services (the "Services") provided by Nodefleet LLC
      ("Nodefleet", "we", "us"), you agree to be bound by these Terms of Service ("Terms"). If you do
      not agree to these Terms, do not use the Services. By creating an account or making your first
      request through our gateway, you confirm that you have read, understood, and accepted these Terms
      and our Privacy Policy.`,
    },
    {
      title: "2. Service Description",
      content: `DataNodes is a high-performance blockchain RPC and REST gateway providing access to
      multiple blockchain networks via API. The Services include API key management, usage analytics,
      MCP integration, and related developer tools. Specific features and supported chains may change
      over time without prior notice.`,
    },
    {
      title: "3. Account Registration",
      content: `To use the Services, you must create an account with accurate, current information.
      You are responsible for maintaining the confidentiality of your account credentials and API keys.
      You are responsible for all activity that occurs under your account, including any usage, billing,
      or actions performed via API keys you have issued. Notify us immediately at nodes@nodefleet.net
      if you suspect unauthorized access to your account or any of your API keys.`,
    },
    {
      title: "4. Acceptable Use",
      content: `You agree not to use the Services to: (a) violate any applicable law or regulation;
      (b) infringe any intellectual-property or privacy right; (c) submit malicious code, conduct DDoS
      attacks, attempt to bypass rate limits or access controls, or otherwise interfere with the
      Services; (d) reverse-engineer, scrape, or resell the Services without express written consent;
      (e) use the Services to transmit unlawful, harmful, or fraudulent content. We may suspend or
      terminate accounts that violate this section without prior notice.`,
    },
    {
      title: "5. API Keys and Security",
      content: `API keys are credentials granting access to your account's quota. Keep them confidential
      and never commit them to public source repositories or expose them client-side. You are
      responsible for traffic generated under your API keys, including from third parties to whom you
      have shared them. We are not liable for losses arising from compromised, leaked, or shared API
      keys. Rotate keys immediately on suspected compromise via the dashboard.`,
    },
    {
      title: "6. Plans, Billing, and Quotas",
      content: `The Services offer a Free plan with usage limits and paid plans with higher limits and
      additional features. Paid plans renew automatically on the billing cycle indicated at checkout
      until cancelled. All charges are processed by Stripe and are non-refundable except where
      required by law. Quota limits apply at the organization level, not per API key. Exceeding plan
      limits may result in throttling, request rejection, or, on overage-enabled plans, additional
      charges. We reserve the right to change pricing with at least thirty (30) days' notice.`,
    },
    {
      title: "7. Service Availability and SLA",
      content: `We aim for high availability but do not guarantee uninterrupted service on the Free or
      lower-tier plans. Custom plans may include written SLAs. Scheduled maintenance, upstream node
      issues, and force-majeure events may cause temporary outages. Our liability for downtime is
      limited as described in Section 11.`,
    },
    {
      title: "8. Termination",
      content: `You may cancel your account at any time from the dashboard or by emailing
      nodes@nodefleet.net. We may suspend or terminate your account for violation of these Terms,
      non-payment, or any conduct we deem harmful to the Services or other users. On termination,
      access to the Services ceases immediately and any pending charges become due. Sections that
      by their nature should survive termination (including Sections 9–13) will continue to apply.`,
    },
    {
      title: "9. Intellectual Property",
      content: `The Services, including all software, designs, logos, and documentation, are owned by
      Nodefleet LLC and protected by intellectual-property laws. We grant you a limited,
      non-exclusive, non-transferable license to use the Services solely as permitted by these Terms.
      You retain ownership of any data you submit through the Services; you grant us a license to
      process that data only as necessary to operate the Services.`,
    },
    {
      title: "10. Disclaimer of Warranties",
      content: `THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,
      WHETHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY,
      FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICES WILL
      BE ERROR-FREE, UNINTERRUPTED, OR SECURE. BLOCKCHAIN NETWORKS HAVE INHERENT RISKS, INCLUDING
      REORGANIZATIONS, FORKS, AND DELAYS, FOR WHICH WE ASSUME NO RESPONSIBILITY.`,
    },
    {
      title: "11. Limitation of Liability",
      content: `TO THE MAXIMUM EXTENT PERMITTED BY LAW, NODEFLEET'S TOTAL LIABILITY ARISING OUT OF OR
      RELATED TO THE SERVICES WILL NOT EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID US IN THE TWELVE
      (12) MONTHS PRECEDING THE CLAIM OR (B) ONE HUNDRED U.S. DOLLARS ($100). IN NO EVENT WILL WE BE
      LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOST
      PROFITS, DATA, OR GOODWILL, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.`,
    },
    {
      title: "12. Indemnification",
      content: `You agree to indemnify, defend, and hold harmless Nodefleet LLC, its officers,
      directors, employees, and agents from any claims, damages, losses, liabilities, costs, and
      expenses (including reasonable attorneys' fees) arising from your use of the Services, your
      violation of these Terms, or your violation of any rights of a third party.`,
    },
    {
      title: "13. Modifications to Terms",
      content: `We may modify these Terms from time to time. Material changes will be communicated by
      email or via in-app notification at least fifteen (15) days before they take effect. Continued
      use of the Services after changes take effect constitutes acceptance of the new Terms. The
      "Last updated" date at the bottom of this document indicates the most recent revision.`,
    },
    {
      title: "14. Governing Law and Disputes",
      content: `These Terms are governed by the laws of the State of Delaware, USA, without regard to
      conflict-of-law principles. Any dispute arising out of or related to these Terms or the
      Services will be resolved exclusively in the state or federal courts located in Delaware. You
      and Nodefleet waive any objection to venue or forum non conveniens.`,
    },
    {
      title: "15. Contact",
      content: `Questions about these Terms? Reach us at:

      Nodefleet LLC
      Email: nodes@nodefleet.net
      Mailing address: 1111B S Governors Ave # 40081, Dover, DE 19904-6903, United States.`,
    },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Hero */}
      <div className="relative h-[40vh] min-h-[300px] overflow-hidden flex items-center justify-center">
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(circle at 30% 30%, rgba(73,183,248,0.18) 0%, rgba(0,0,0,0) 60%), radial-gradient(circle at 70% 70%, rgba(115,103,240,0.18) 0%, rgba(0,0,0,0) 60%), #0a0a0a",
          }}
        />
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative z-10 text-center px-4 max-w-3xl mx-auto"
        >
          <h1 className="font-title text-4xl md:text-5xl font-bold text-white mb-4">
            Terms of <span className="text-primary">Service</span>
          </h1>
          <p className="font-secondary text-base md:text-lg text-gray-300 max-w-2xl mx-auto">
            The agreement between you and Nodefleet for using the DataNodes RPC + REST gateway and
            related services.
          </p>
        </motion.div>
      </div>

      {/* Body */}
      <div className="bg-landing-section">
        <motion.div
          className="max-w-4xl mx-auto px-4 md:px-8 py-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="bg-[#16142D]/60 border border-white/[0.06] rounded-sm p-6 md:p-10">
            <p className="text-gray-400 text-sm mb-2">Last updated: May 8, 2026</p>
            <p className="text-gray-300 mb-8">
              These Terms of Service ("Terms") govern your use of the DataNodes services provided
              by Nodefleet LLC By creating an account, accessing the Services, or making your
              first API request, you accept these Terms in full. If you do not agree, do not use
              the Services.
            </p>

            {sections.map((section, index) => (
              <motion.div
                key={index}
                className="mb-8"
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.35, delay: index * 0.04 }}
              >
                <h2 className="font-title text-xl md:text-2xl font-semibold text-white mb-3">
                  {section.title}
                </h2>
                <p className="font-secondary text-gray-300 leading-relaxed text-sm md:text-base whitespace-pre-line">
                  {section.content}
                </p>
              </motion.div>
            ))}

            <div className="mt-12 border-t border-white/[0.06] pt-6 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
              <p className="text-xs text-gray-500">
                Questions? Email{" "}
                <a
                  href="mailto:nodes@nodefleet.net"
                  className="text-primary underline underline-offset-2 hover:text-primary-hover"
                >
                  nodes@nodefleet.net
                </a>
                .
              </p>
              <div className="flex gap-3 text-xs text-gray-400">
                <Link to="/privacy-policy" className="hover:text-white">Privacy Policy</Link>
                <span className="text-gray-600">·</span>
                <Link to="/" className="hover:text-white">Back to home</Link>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TermsOfService;
